import { ApolloError } from '@apollo/client';
import {
  BatteryPowerType,
  ChartColorContext,
  ChartErrorComponent,
  defaultEChartsOptionForInterval,
  getBatteryPowerLinesOption,
  ECHARTS_CONTAINER_HEIGHT as height,
  nowMarkLineSeriesOption,
} from '@e2m-customer-portal/domains';
import { useLocale } from '@e2m-customer-portal/hooks';
import { convertMeasure, InputFieldType, isDefined, isMeasureWithValue, Nullable } from '@e2m-customer-portal/shared';
import { EChartsOption, EChartsType, SeriesOption } from 'echarts';
import { Device, DeviceDatapoint, DeviceTimeSeries, Forecast, ForecastSlice, Interval } from 'generated/graphql';
import { ReactElement, useContext, useMemo, useRef } from 'react';
import EChartsReact, { EChartsReactRef } from 'react-echarts-library';
import { useTranslation } from 'react-i18next';
import { ChartColor, getOutages, getOutagesData, getSortedDeviceTimeSeries } from './utils';
import { Button } from '@mui/material';
import { ZoomOutIcon } from 'assets/icons';
import { MarkAreaOption } from 'echarts/types/dist/shared';

function powerFeedForecast(slices: readonly ForecastSlice[], unit: string = 'kW'): Array<[number, number | null]> {
  return slices.map((slice) => {
    const field = slice.fields.find((field) => field.type === InputFieldType.POWER_FEED);

    const fieldValue = isMeasureWithValue(field?.value) ? convertMeasure(field!.value, unit).value : null;

    return [slice.date.getTime(), fieldValue];
  });
}

function actualPowerDatapoints(
  datapoints: ReadonlyArray<DeviceDatapoint>,
  unit: string = 'kW',
): Array<[number, Nullable<number>]> {
  return datapoints.map((datapoint) => [
    datapoint.date.getTime(),
    convertMeasure({ value: datapoint.actualPower, unit: 'MW' }, unit).value,
  ]);
}

interface PowerFeedChartProps {
  dataInterval: Interval;
  device: Device;
  forecasts?: ReadonlyArray<Forecast>;
  forecastsError?: ApolloError;
  deviceTimeSeries?: DeviceTimeSeries[];
  deviceTimeSeriesError?: ApolloError;
  power?: BatteryPowerType;
  onChartReadyCallback: (instance: EChartsType) => void;
  resetZoom: (ref: EChartsReactRef) => void;
  isZoomed?: boolean;
}

export function PowerFeedChart({
  dataInterval,
  device,
  forecasts,
  forecastsError,
  deviceTimeSeries,
  deviceTimeSeriesError,
  power,
  onChartReadyCallback,
  resetZoom,
  isZoomed,
}: Readonly<PowerFeedChartProps>): ReactElement<PowerFeedChartProps> {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const chartColorVariant = useContext(ChartColorContext);
  const chartRef = useRef<EChartsReactRef>(null);

  const sortedDatapoints = getSortedDeviceTimeSeries(device, deviceTimeSeries);

  const forecast =
    [
      ...(
        forecasts?.find((forecast) => forecast.deviceId === device.id) ?? {
          slices: [],
        }
      ).slices,
    ].sort((a, b) => a.date.getTime() - b.date.getTime()) ?? [];

  const powerFeedData = powerFeedForecast(forecast);
  const outagesData = getOutagesData(forecast);
  const outagesMarkedAreas = getOutages(forecast);

  const hasActualPowerData = sortedDatapoints.some((datapoint) => datapoint.actualPower != null);
  const batteryPowerLinesOption = useMemo(
    () => getBatteryPowerLinesOption(t, dataInterval, power, true),
    [dataInterval, power],
  );

  const feedSeries = {
    data: powerFeedData,
    type: 'line',
    step: 'end',
    symbol: 'circle',
    showSymbol: false,
    tooltip: {
      valueFormatter: (value) => (value == null ? '-' : `${Number(value).toLocaleString(locale)} kW`),
    },
  } satisfies SeriesOption;

  const defaultOption = defaultEChartsOptionForInterval(t, dataInterval);
  const option: EChartsOption = {
    ...defaultOption,
    group: 'group2',
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          formatter: '{value} kW',
          color: '#888888',
        },
        splitLine: {
          lineStyle: {
            color: '#F0F0F0',
          },
        },
        boundaryGap: ['0.1%', '0.1%'],
      },
    ],
    series: [
      {
        ...feedSeries,
        name: t('MARKET_LOCATION_FEED_IN_CHART_FORECASTED'),
        color: ChartColor.PrimaryFull[chartColorVariant],
      },
      hasActualPowerData
        ? {
            ...feedSeries,
            data: actualPowerDatapoints(sortedDatapoints),
            name: t('MARKET_LOCATION_FEED_IN_CHART_MEASURED_VPP'),
            color: ChartColor.SecondaryFull[chartColorVariant],
          }
        : undefined,
      ...(batteryPowerLinesOption ?? []),
      nowMarkLineSeriesOption,
      outagesData.some((item) => item !== null)
        ? ({
            type: 'line',
            lineStyle: {
              width: 0,
            },
            name: t('DOWNTIME_SET'),
            connectNulls: false,
            symbol: 'square',
            showSymbol: false,
            tooltip: {
              valueFormatter: () => '',
            },
            color: '#FF0A1180',
            data: outagesData,
            markArea: {
              label: { show: false },
              z: 9,
              itemStyle: {
                color: '#FF0A11',
                opacity: 0.5,
              },
              data: outagesMarkedAreas
                .map((s) => {
                  if (s.isOutage) return [{ xAxis: +s.interval.start }, { xAxis: +s.interval.end }];
                })
                .filter(isDefined),
            } as MarkAreaOption,
          } satisfies SeriesOption)
        : (undefined as unknown as SeriesOption),
    ].filter(isDefined),
  };

  return (
    <div className="relative">
      {isZoomed && (
        <Button
          className="absolute top-8 left-32 w-fit px-1 bg-white/45 z-40"
          style={{ color: ChartColor.PrimaryFull[chartColorVariant] }}
          color="inherit"
          variant="outlined"
          onClick={() => chartRef.current && resetZoom(chartRef.current)}
        >
          <ZoomOutIcon style={{ fill: ChartColor.PrimaryFull[chartColorVariant] }} />
        </Button>
      )}
      <EChartsReact
        style={{ height }}
        option={option}
        opts={{ locale, renderer: 'svg' }}
        data-testid="power-feed-chart"
        onChartReady={onChartReadyCallback}
        ref={chartRef}
      />
      {(deviceTimeSeriesError || forecastsError) && (
        <ChartErrorComponent errors={[deviceTimeSeriesError, forecastsError]} />
      )}
    </div>
  );
}
